import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../../supabaseClient";
import { useTheme } from "../theme/ThemeContext.jsx";

const REFRESH_MS = 30000;

const TeamStats = ({ team }) => {
  const playerGroups = new Map();
  team.players?.forEach((player) => {
    player.groups?.forEach((group) => {
      if (!playerGroups.has(group.name)) playerGroups.set(group.name, []);
      playerGroups.get(group.name).push({ ...player, stats: group.stats });
    });
  });

  return (
    <section className="mb-4">
      <h6>{team.name} {team.score !== undefined && `— ${team.score}`}</h6>
      {team.stats?.map((group) => (
        <p key={group.name} className="small mb-2">
          <strong>{group.name}: </strong>
          {group.stats.map((stat) => `${stat.label} ${stat.value}`).join(" · ")}
        </p>
      ))}
      {[...playerGroups.entries()].map(([groupName, players]) => {
        const labels = [...new Set(players.flatMap((player) => player.stats.map((stat) => stat.label)))];
        return (
          <div className="table-responsive mb-3" key={groupName}>
            <h6 className="small text-uppercase">{groupName}</h6>
            <table className="table table-sm table-striped mb-0">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>#</th>
                  <th>Pos</th>
                  {labels.map((label) => <th key={label}>{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {players.map((player, index) => (
                  <tr key={`${player.name}-${index}`}>
                    <td className="text-start">{player.name}</td>
                    <td>{player.number}</td>
                    <td>{player.position}</td>
                    {labels.map((label) => (
                      <td key={label}>{player.stats.find((stat) => stat.label === label)?.value ?? "—"}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
      {!team.players?.length && !team.stats?.length && <p className="small">No player stats are available yet.</p>}
    </section>
  );
};

const SportsGameStatsModal = ({ show, onClose, gameId, sport, title }) => {
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!show || !gameId) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const { data: result, error: invokeError } = await supabase.functions.invoke("sports-game-stats", {
          body: { sport, gameId },
        });
        if (cancelled) return;
        if (invokeError || result?.error) {
          setError("Unable to load game statistics.");
        } else {
          setError("");
          setData(result);
        }
      } catch {
        if (!cancelled) setError("Unable to load game statistics.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    setLoading(true);
    setError("");
    setData(null);
    load();
    const timer = window.setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [show, gameId, sport]);

  if (!show) return null;
  const isDark = theme === "dark";

  return createPortal((
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${title} game stats`}
      onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 2050, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem", background: "rgba(0,0,0,.55)" }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{ background: isDark ? "#222" : "#fff", color: isDark ? "#fff" : "#000", border: `1px solid ${isDark ? "#444" : "#ddd"}`, borderRadius: 8, padding: "1rem", width: "min(94vw, 760px)", maxHeight: "82vh", overflowY: "auto", overflowX: "hidden", boxSizing: "border-box" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{title} — {sport} Stats</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
        </div>
        {data?.period && <p className="small">{data.period}{data.status ? ` · ${data.status}` : ""}</p>}
        {loading && !data && <p>Loading statistics…</p>}
        {error && <p className="text-danger">{error}</p>}
        {data?.teams?.length ? data.teams.map((team) => <TeamStats key={team.abbreviation || team.name} team={team} />) : data && <p>No player statistics are available yet.</p>}
      </div>
    </div>
  ), document.body);
};

export default SportsGameStatsModal;
